import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CreateUserRequest } from '../../types';
import { usersService, ApiUser } from '../../services/usersService';

// Updated UsersState to use ApiUser
type RoleBreakdown = {
  admin: number;
  packer: number;
  receiver: number;
  security: number;
};

interface UsersState {
  list: ApiUser[];
  total: number;
  stats: {
    total: { count: number; breakdown: RoleBreakdown };
    active: { count: number; breakdown: RoleBreakdown };
    disabled: { count: number; breakdown: RoleBreakdown };
  };
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: UsersState = {
  list: [],
  total: 0,
  stats: {
    total: { count: 0, breakdown: { admin: 0, packer: 0, receiver: 0, security: 0 } },
    active: { count: 0, breakdown: { admin: 0, packer: 0, receiver: 0, security: 0 } },
    disabled: { count: 0, breakdown: { admin: 0, packer: 0, receiver: 0, security: 0 } },
  },
  loading: false,
  error: null,
};

function computeStats(users: ApiUser[]) {
  const countByRole = (arr: ApiUser[]): RoleBreakdown => {
    const base: RoleBreakdown = { admin: 0, packer: 0, receiver: 0, security: 0 };
    for (const u of arr) {
      const key = (u.role ?? '').toString().trim().toLowerCase();
      if (key === 'admin') base.admin += 1;
      else if (key === 'packer') base.packer += 1;
      else if (key === 'receiver') base.receiver += 1;
      else if (key === 'security' || key === 'security guard') base.security += 1;
    }
    return base;
  };

  const totalCount = users.length;
  const activeUsers = users.filter(u => u.isactive === 'Y');
  const disabledUsers = users.filter(u => u.isactive === 'N');

  return {
    total: { count: totalCount, breakdown: countByRole(users) },
    active: { count: activeUsers.length, breakdown: countByRole(activeUsers) },
    disabled: { count: disabledUsers.length, breakdown: countByRole(disabledUsers) },
  };
}

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const { data, rowCount } = await usersService.getUsers();
      return { data, rowCount };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch users');
    } 
  }
);

export const createUser = createAsyncThunk(
  'users/create',
  async (userData: CreateUserRequest, { rejectWithValue }) => {
    try {
      const newUser = await usersService.createUser(userData);
      return newUser;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/update',
  async ({ id, data }: { id: string; data: Partial<ApiUser> }, { rejectWithValue, getState }) => {
    try {
      // Get the current auth state to access user ID
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      // Ensure modifiedby is set from auth state
      const userDataWithModifier = {
        ...data,
        modifiedby: currentUser?.userid || 'SYSTEM'
      };
      
      const updatedUser = await usersService.updateUser(id, userDataWithModifier);
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/delete',
  async (id: string, { rejectWithValue, getState }) => {
    try {
      // Get the current auth state to access user ID
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      // Pass the user ID to the service method for audit trail
      await usersService.deleteUser(id, currentUser?.userid || 'SYSTEM');
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete user');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'users/toggleStatus',
  async (id: string, { rejectWithValue, getState }) => {
    try {
      // Get the current auth state to access user ID
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      // Pass the user ID to the service method for audit trail
      const updatedUser = await usersService.toggleUserStatus(id, currentUser?.userid || 'SYSTEM');
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to toggle user status');
    }
  }
);

// Slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data as ApiUser[];
        // Use rowCount if provided, otherwise fallback to data length
        state.total = (action.payload.rowCount as number | undefined) ?? (state.list?.length || 0);
        state.stats = computeStats(state.list);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
        state.total = state.list.length;
        state.stats = computeStats(state.list);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(user => user.userid === action.payload.userid);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.stats = computeStats(state.list);
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(user => user.userid !== action.payload);
        state.total = state.list.length;
        state.stats = computeStats(state.list);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle user status
      .addCase(toggleUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(user => user.userid === action.payload.userid);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.stats = computeStats(state.list);
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setLoading } = usersSlice.actions;
export default usersSlice.reducer;
